import { useEffect, useId, useMemo, useRef, useState } from "react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface FilterMultiSelectProps {
  /** Short facet label shown on the trigger, e.g. "Teacher". */
  label: string;
  options: MultiSelectOption[];
  /** Currently selected option values. */
  selected: string[];
  onToggle: (value: string) => void;
  /** Disable the whole control (e.g. student facet before a run completes). */
  disabled?: boolean;
  /** Hint shown when there are no options (e.g. "No students in this run"). */
  emptyHint?: string;
  /** Placeholder for the in-panel search box. */
  searchPlaceholder?: string;
  "data-testid"?: string;
}

/**
 * A self-contained multi-select dropdown built on the repo's own primitives
 * (no Radix). The panel is a `role="listbox"` with roving-tabindex options:
 * ArrowUp/Down + Home/End move the active option, Enter/Space toggles it,
 * Escape closes and returns focus to the trigger, and an outside click closes.
 * A search box filters the (potentially long) option list, doubling as the
 * type-ahead the large-roster student facet reuses.
 */
export function FilterMultiSelect({
  label,
  options,
  selected,
  onToggle,
  disabled = false,
  emptyHint = "No options",
  searchPlaceholder = "Search…",
  "data-testid": testId,
}: FilterMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  // Snapshot of what was selected when the panel opened. Selected options pin to
  // the top (easy to find + unselect), but only re-sort on the NEXT open — so
  // toggling doesn't reshuffle rows out from under the cursor mid-selection.
  const [pinnedAtOpen, setPinnedAtOpen] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Match the underlying value as well as the label so an option whose label
    // differs from its key stays findable by either — e.g. a student shown by
    // name is still searchable by their email (the identifier value).
    const filtered = q
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(q) ||
            o.value.toLowerCase().includes(q),
        )
      : options;
    // Stable partition: options selected when the panel opened float to the top,
    // preserving the incoming (alphabetical) order within each group.
    return [...filtered].sort(
      (a, b) =>
        (pinnedAtOpen.has(a.value) ? 0 : 1) -
        (pinnedAtOpen.has(b.value) ? 0 : 1),
    );
  }, [options, query, pinnedAtOpen]);

  // Clamp the active index to the (possibly search-shrunk) list at read time
  // rather than syncing it via an effect.
  const maxIndex = Math.max(0, visible.length - 1);
  const active = Math.min(activeIndex, maxIndex);
  const optionId = (idx: number) => `${listId}-opt-${idx}`;
  // Focus stays in the search input; the active option is conveyed to screen
  // readers via aria-activedescendant (not roving DOM focus).
  const activeId = visible.length > 0 ? optionId(active) : undefined;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Keep the active option scrolled into view as the user arrows through it.
  useEffect(() => {
    if (!open || !activeId) return;
    // `?.scrollIntoView?.` — jsdom leaves scrollIntoView undefined.
    document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeId]);

  const close = (returnFocus = true) => {
    setOpen(false);
    setQuery("");
    if (returnFocus) triggerRef.current?.focus();
  };

  const openPanel = () => {
    setPinnedAtOpen(new Set(selected));
    setActiveIndex(0);
    setOpen(true);
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(active + 1, maxIndex));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(active - 1, 0));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIndex(maxIndex);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = visible[active];
      if (opt) onToggle(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  const count = selected.length;

  return (
    <div ref={rootRef} className="relative inline-block" data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // A disabled trigger can't open to reveal the in-panel empty hint, so
        // surface it as the button's tooltip + accessible label — the only way
        // the "why is this disabled?" reason (e.g. "No run yet" vs "No grades in
        // roster") reaches sighted and screen-reader users alike.
        title={disabled ? emptyHint : undefined}
        aria-label={disabled ? `${label}: ${emptyHint}` : undefined}
        onClick={() => (open ? close(false) : openPanel())}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-2/60 px-3 py-1.5 text-xs font-medium text-text-soft hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{label}</span>
        {count > 0 && (
          <span
            data-testid={testId ? `${testId}-count` : undefined}
            className="rounded-full bg-brand px-1.5 text-[10px] font-semibold text-white"
          >
            {count}
          </span>
        )}
        <span aria-hidden="true" className="text-text-muted">
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-64 rounded-md border border-border-subtle bg-surface shadow-lg">
          <div className="p-2">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onListKeyDown}
              placeholder={searchPlaceholder}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={activeId}
              aria-autocomplete="list"
              aria-label={`Search ${label}`}
              data-testid={testId ? `${testId}-search` : undefined}
              className="w-full rounded border border-border-subtle bg-page px-2 py-1 text-xs text-white placeholder:text-text-muted focus:border-brand focus:outline-none"
            />
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-label={label}
            className="max-h-56 overflow-y-auto px-1 pb-1"
          >
            {visible.length === 0 ? (
              <li className="px-2 py-2 text-xs text-text-muted">{emptyHint}</li>
            ) : (
              visible.map((opt, idx) => {
                const isSelected = selected.includes(opt.value);
                const isActive = idx === active;
                return (
                  <li
                    key={opt.value}
                    id={optionId(idx)}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => onToggle(opt.value)}
                    data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                    className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs ${
                      isActive ? "bg-surface-2" : ""
                    } text-text-soft`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-3.5 w-3.5 flex-none items-center justify-center rounded-sm border text-[9px] ${
                        isSelected
                          ? "border-brand bg-brand text-white"
                          : "border-border-subtle"
                      }`}
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
